import type {
  ActiveIsoDashboard,
  AddCompInput,
  AddCompResponse,
  BidResponse,
  BidSummary,
  BlastDetail,
  BlastListResponse,
  CaptureLiveIsoInput,
  CaptureLiveIsoResponse,
  CloseDealInput,
  CloseOutInput,
  CompImportInput,
  CompImportResult,
  ConfirmSuggestedBidInput,
  CreateBlastInput,
  CreateDealInput,
  DealFeedbackInput,
  DealFeedbackResponse,
  CreateDealerInput,
  CreateDealerResponse,
  CreateDealerContactInput,
  CreateInteractionInput,
  CreateIsoPrefInput,
  CreateOfferInput,
  CreateSubmissionResponse,
  DealBoardResponse,
  DealDetail,
  DealMutationResponse,
  Dealer,
  DealerConsentInput,
  DealerContactWithConsent,
  DealerDetail,
  DealerImportInput,
  DealerImportResult,
  DealerInteraction,
  DealerIsoPref,
  DealerListQuery,
  DealerListResponse,
  DealerOnboardInput,
  DecodeResult,
  DictionaryResolveResponse,
  EnterBidInput,
  FunnelDashboard,
  HealthResponse,
  LoginRequest,
  LoginResponse,
  LiquidityTower,
  LogInteractionResponse,
  MatchesResponse,
  MeResponse,
  Offer,
  OfferBuilder,
  OnboardDealerResponse,
  OperatorCreateSubmissionInput,
  PipelineBoard,
  PresentOffersResponse,
  QualifyResponse,
  SellerSubmission,
  SendBlastResponse,
  SpecReview,
  SubmissionDetail,
  SubmissionListQuery,
  SubmissionListResponse,
  SuggestBidInput,
  UpdateBidInput,
  UpdateDealInput,
  UpdateDealerInput,
  UpdateInteractionInput,
  UpdateIsoPrefInput,
  UpdateSubmissionInput,
  UpdateVehicleSpecInput,
  UpsertQualificationInput,
  ValuationResponse,
  SaveValuationInput,
} from '@motoveo/shared';

/** POST /api/blast-recipients/:id/suggest-bid response. */
export interface SuggestBidResponse {
  review_task_id: string;
  blast_recipient_id: string;
  dealer_id: string;
  suggestion: {
    kind: 'cash' | 'pass' | 'ambiguous';
    bidType: string | null;
    amountCents: number | null;
    passReason: string | null;
    confidence: string;
    ambiguous: boolean;
    raw: string;
  } | null;
  /** M15/M7 — >1 recent ACTIVE blast leg matched the sender; confirm must pick a candidate. */
  attribution_ambiguous?: boolean;
  attribution_candidates?: Array<{
    blast_recipient_id: string;
    blast_id: string;
    dealer_id: string;
    vehicle_id: string;
    last_outbound_at: string;
  }>;
}

/** Thin fetch wrapper. Paths are relative — the dev proxy / Caddy route them to the API. */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body?.error?.message) message = body.error.message;
    } catch {
      // non-JSON error body; keep the status message
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

function submissionsQuery(q: Partial<SubmissionListQuery>): string {
  const params = new URLSearchParams();
  if (q.status) params.set('status', q.status);
  if (q.source) params.set('source', q.source);
  if (q.limit != null) params.set('limit', String(q.limit));
  if (q.offset != null) params.set('offset', String(q.offset));
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const api = {
  health: () => request<HealthResponse>('/health'),
  me: () => request<MeResponse>('/auth/me'),
  login: (body: LoginRequest) => request<LoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  submissions: {
    list: (q: Partial<SubmissionListQuery> = {}) =>
      request<SubmissionListResponse>(`/api/submissions${submissionsQuery(q)}`),
    get: (id: string) => request<SubmissionDetail>(`/api/submissions/${id}`),
    create: (body: OperatorCreateSubmissionInput) =>
      request<CreateSubmissionResponse>('/api/submissions', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpdateSubmissionInput) =>
      request<SellerSubmission>(`/api/submissions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** M10 — upsert the seller-expectation qualification (the launch-gate + control-tower input). */
    setQualification: (id: string, body: UpsertQualificationInput) =>
      request<QualifyResponse>(`/api/submissions/${id}/qualification`, { method: 'PUT', body: JSON.stringify(body) }),
    /** operator "decode now" — synchronous vPIC decode + spec resolve. */
    decode: (id: string) => request<DecodeResult>(`/api/submissions/${id}/decode`, { method: 'POST' }),
    /** authed raw-bytes URL — usable directly as an <img src>. */
    mediaRawUrl: (id: string, mediaId: string) => `/api/submissions/${id}/media/${mediaId}/raw`,
  },

  vehicles: {
    /** the operator spec-review packet (spec + options + provenance + tasks). */
    spec: (id: string) => request<SpecReview>(`/api/vehicles/${id}/spec`),
    /** operator override — wins all decode sources; re-resolves the spec. */
    updateSpec: (id: string, patch: UpdateVehicleSpecInput) =>
      request<DecodeResult>(`/api/vehicles/${id}/spec`, { method: 'PATCH', body: JSON.stringify(patch) }),
    /** operator-triggered paid sticker decode (~$9/VIN). */
    decodePaid: (id: string, force = false) =>
      request<DecodeResult>(`/api/vehicles/${id}/decode/paid${force ? '?force=true' : ''}`, { method: 'POST' }),
  },

  dictionary: {
    resolve: (code: string, generation?: string, year?: number) => {
      const p = new URLSearchParams({ code });
      if (generation) p.set('generation', generation);
      if (year != null) p.set('year', String(year));
      return request<DictionaryResolveResponse>(`/api/dictionary/resolve?${p.toString()}`);
    },
  },

  dealers: {
    list: (q: Partial<DealerListQuery> = {}) => {
      const p = new URLSearchParams();
      if (q.status) p.set('status', q.status);
      if (q.state) p.set('state', q.state);
      if (q.q) p.set('q', q.q);
      if (q.pipeline_stage) p.set('pipeline_stage', q.pipeline_stage);
      if (q.relationship_owner_id) p.set('relationship_owner_id', q.relationship_owner_id);
      if (q.limit != null) p.set('limit', String(q.limit));
      const s = p.toString();
      return request<DealerListResponse>(`/api/dealers${s ? `?${s}` : ''}`);
    },
    get: (id: string) => request<DealerDetail>(`/api/dealers/${id}`),
    create: (body: CreateDealerInput) =>
      request<CreateDealerResponse>('/api/dealers', { method: 'POST', body: JSON.stringify(body) }),
    onboard: (body: DealerOnboardInput) =>
      request<OnboardDealerResponse>('/api/dealers/onboard', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpdateDealerInput) =>
      request<Dealer>(`/api/dealers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    addContact: (id: string, body: CreateDealerContactInput) =>
      request<{ id: string }>(`/api/dealers/${id}/contacts`, { method: 'POST', body: JSON.stringify(body) }),
    captureConsent: (id: string, body: { contact_id: string } & Partial<DealerConsentInput>) =>
      request<{ id: string; contact_id: string }>(`/api/dealers/${id}/consent`, { method: 'POST', body: JSON.stringify(body) }),
    updateContact: (contactId: string, body: Partial<CreateDealerContactInput>) =>
      request<DealerContactWithConsent>(`/api/dealer-contacts/${contactId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    addIsoPref: (id: string, body: CreateIsoPrefInput) =>
      request<DealerIsoPref>(`/api/dealers/${id}/iso-prefs`, { method: 'POST', body: JSON.stringify(body) }),
    updateIsoPref: (prefId: string, body: UpdateIsoPrefInput) =>
      request<DealerIsoPref>(`/api/iso-prefs/${prefId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    // M9 recruitment cockpit:
    logInteraction: (id: string, body: CreateInteractionInput) =>
      request<LogInteractionResponse>(`/api/dealers/${id}/interactions`, { method: 'POST', body: JSON.stringify(body) }),
    updateInteraction: (interactionId: string, body: UpdateInteractionInput) =>
      request<DealerInteraction>(`/api/dealer-interactions/${interactionId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    captureLiveIso: (id: string, body: CaptureLiveIsoInput) =>
      request<CaptureLiveIsoResponse>(`/api/dealers/${id}/live-iso`, { method: 'POST', body: JSON.stringify(body) }),
    import: (body: DealerImportInput) =>
      request<DealerImportResult>('/api/dealers/import', { method: 'POST', body: JSON.stringify(body) }),
  },

  /** M11 — the founder valuation / appraisal worksheet (comps + retail range + dealer-acq band + verdict). */
  valuation: {
    /** the live worksheet for a submission (retail range · dealer-acq band · verdict · comps). */
    get: (submissionId: string) => request<ValuationResponse>(`/api/submissions/${submissionId}/valuation`),
    /** recompute with operator assumption overrides, persist, and optionally wire the launch gate. */
    save: (submissionId: string, body: SaveValuationInput) =>
      request<ValuationResponse>(`/api/submissions/${submissionId}/valuation`, { method: 'PUT', body: JSON.stringify(body) }),
    /** add one external comp by hand. */
    addComp: (body: AddCompInput) => request<AddCompResponse>('/api/comps', { method: 'POST', body: JSON.stringify(body) }),
    /** bulk CSV comp import (dry-run by default; idempotent on source_url). */
    importComps: (body: CompImportInput) => request<CompImportResult>('/api/comps/import', { method: 'POST', body: JSON.stringify(body) }),
    /** delete an external/manual comp. */
    deleteComp: (compId: string) => request<null>(`/api/comps/${compId}`, { method: 'DELETE' }),
    /** M15 — audit-void a POISONED comp (works on moat bid/deal comps; reason required; never deleted). */
    voidComp: (compId: string, reason: string) =>
      request<{ id: string; voided_at: string }>(`/api/comps/${compId}/void`, { method: 'POST', body: JSON.stringify({ reason }) }),
  },

  cockpit: {
    activeIsos: () => request<ActiveIsoDashboard>('/api/cockpit/active-isos'),
    pipeline: () => request<PipelineBoard>('/api/cockpit/pipeline'),
    /** M10 liquidity control tower — per-submission launch readiness + bid picture + close outcome. */
    liquidity: (includeClosed = false) =>
      request<LiquidityTower>(`/api/cockpit/liquidity${includeClosed ? '?include_closed=true' : ''}`),
    /** M12 funnel / learning dashboard — fleet-wide demand + close-out + NPS + per-dealer reliability. */
    funnel: () => request<FunnelDashboard>('/api/cockpit/funnel'),
  },

  matches: {
    forSubmission: (submissionId: string) => request<MatchesResponse>(`/api/submissions/${submissionId}/matches`),
  },

  blasts: {
    /** create + preview: ranked matches → blast_recipients + the masked packet. */
    create: (submissionId: string, body: CreateBlastInput = {}) =>
      request<BlastDetail>(`/api/submissions/${submissionId}/blasts`, { method: 'POST', body: JSON.stringify(body) }),
    listForSubmission: (submissionId: string) => request<BlastListResponse>(`/api/submissions/${submissionId}/blasts`),
    get: (id: string) => request<BlastDetail>(`/api/blasts/${id}`),
    /** fan out through the ONE compliance gate (per-recipient send_status set from the gate result). */
    send: (id: string) => request<SendBlastResponse>(`/api/blasts/${id}/send`, { method: 'POST' }),
  },

  bids: {
    /** operator enter/confirm a dealer bid on a recipient (writes a comp; flips response_type). */
    enter: (recipientId: string, body: EnterBidInput) =>
      request<BidResponse>(`/api/blast-recipients/${recipientId}/bid`, { method: 'POST', body: JSON.stringify(body) }),
    /** parse an inbound SMS into a SUGGESTED bid (no auto-accept). */
    suggest: (recipientId: string, body: SuggestBidInput) =>
      request<SuggestBidResponse>(`/api/blast-recipients/${recipientId}/suggest-bid`, { method: 'POST', body: JSON.stringify(body) }),
    /** confirm a suggested bid → records it. */
    confirmSuggested: (reviewTaskId: string, body: ConfirmSuggestedBidInput = {}) =>
      request<BidResponse>(`/api/suggested-bids/${reviewTaskId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    update: (bidId: string, body: UpdateBidInput) =>
      request<BidSummary>(`/api/bids/${bidId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },

  offers: {
    /** the offer-menu builder: offers + candidate bids + the exact masked seller preview. */
    forSubmission: (submissionId: string) => request<OfferBuilder>(`/api/submissions/${submissionId}/offers`),
    /** curate one priced bid into an offer (Tier-1 pass-through enforced). */
    add: (submissionId: string, body: CreateOfferInput) =>
      request<OfferBuilder>(`/api/submissions/${submissionId}/offers`, { method: 'POST', body: JSON.stringify(body) }),
    setRank: (offerId: string, rank: number | null) =>
      request<Offer>(`/api/offers/${offerId}`, { method: 'PATCH', body: JSON.stringify({ rank }) }),
    withdraw: (offerId: string) => request<Offer>(`/api/offers/${offerId}`, { method: 'PATCH', body: JSON.stringify({ status: 'withdrawn' }) }),
    /** present the menu: mint the seller magic-link + send it. */
    present: (submissionId: string) =>
      request<PresentOffersResponse>(`/api/submissions/${submissionId}/offers/present`, { method: 'POST' }),
  },

  deals: {
    list: () => request<DealBoardResponse>('/api/deals'),
    get: (id: string) => request<DealDetail>(`/api/deals/${id}`),
    create: (body: CreateDealInput) => request<DealMutationResponse>('/api/deals', { method: 'POST', body: JSON.stringify(body) }),
    transition: (id: string, body: UpdateDealInput) => request<DealMutationResponse>(`/api/deals/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** the explicit close-out step (won/lost/fell-through) — a won writes the realized-sale comp. */
    close: (id: string, body: CloseDealInput) => request<DealDetail>(`/api/deals/${id}/close`, { method: 'POST', body: JSON.stringify(body) }),
    /** M12 — record/update close-out attribution (fallout reason, confirmations, testimonial) after the fact. */
    closeOut: (id: string, body: CloseOutInput) => request<DealDetail>(`/api/deals/${id}/close-out`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** M12 — upsert one side's NPS (0–10) + comment (party seller|dealer). */
    addFeedback: (id: string, body: DealFeedbackInput) => request<DealFeedbackResponse>(`/api/deals/${id}/feedback`, { method: 'POST', body: JSON.stringify(body) }),
  },
};
